import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Select,
  Switch,
  InputNumber,
  Button,
  message,
  Row,
  Col,
  Divider,
  Statistic,
  Table,
  Tag,
  Space,
  Typography,
  Alert,
  Spin,
  theme,
} from 'antd';
import {
  SettingOutlined,
  BarChartOutlined,
  RobotOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  ApiOutlined,
} from '@ant-design/icons';
import { userPreferencesApi, modelSelectionApi, modelApi } from '../../services/api';
import { 
  UserModelPreferences, 
  UserModelUsageStats, 
  ModelOption,
  ModelStat,
  DailyStat 
} from '../../types';
import { usePermissionCheck, PermissionGuard } from '../../context/PermissionContext';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const UsageInfo: React.FC = () => {
  const { token } = theme.useToken();
  const [statsLoading, setStatsLoading] = useState(false);
  const [usageStats, setUsageStats] = useState<UserModelUsageStats | null>(null);
  const [supportedModels, setSupportedModels] = useState<ModelOption[]>([]);

  // 权限检查
  const { canViewPreferences, canViewPreferenceStats } = usePermissionCheck();

  // 加载使用统计
  const loadUsageStats = async () => {
    try {
      setStatsLoading(true);
      const response = await userPreferencesApi.getMyUsageStats();
      setUsageStats(response.data.data);
    } catch (error: any) {
      message.error('加载使用统计失败: ' + (error.response?.data?.error || error.message));
    } finally {
      setStatsLoading(false);
    }
  };

  // 加载支持的模型列表
  const loadSupportedModels = async () => {
    try {
      try {
        const response = await modelSelectionApi.getSupportedModels();
        setSupportedModels(response.data.models || []);
      } catch (error) {
        const response = await modelApi.getModels(1, 100);
        const models = response.data.data?.models || [];
        const modelOptions: ModelOption[] = models.map(model => ({
          name: model.model_name,
          display_name: model.display_name,
          provider: model.provider,
          type: 'text' as const,
          max_tokens: model.max_tokens,
          input_price: model.input_price,
          output_price: model.output_price,
          is_enabled: model.is_enabled,
          description: model.description,
        }));
        setSupportedModels(modelOptions);
      }
    } catch (error: any) {
      console.error('加载模型列表失败:', error);
    }
  };

  useEffect(() => {
    loadUsageStats();
    loadSupportedModels();
  }, []);

  // 模型统计表格列
  const modelStatsColumns = [
    {
      title: '模型名称',
      dataIndex: 'modelName',
      key: 'modelName',
      render: (name: string) => {
        const model = supportedModels.find(m => m.name === name);
        return (
          <Space>
            <RobotOutlined />
            <div>
              <div style={{ fontWeight: 500 }}>{model?.display_name || name}</div>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {model?.provider || 'unknown'}
              </Text>
            </div>
          </Space>
        );
      },
    },
    {
      title: '调用次数',
      dataIndex: 'requests',
      key: 'requests',
      render: (count: number) => <Statistic value={count} />,
    },
    {
      title: 'Token使用',
      dataIndex: 'tokens',
      key: 'tokens',
      render: (tokens: number) => <Statistic value={tokens} suffix="tokens" />,
    },
    {
      title: '总花费',
      dataIndex: 'cost',
      key: 'cost',
      render: (cost: number) => (
        <Statistic value={cost} prefix="¥" precision={4} />
      ),
    },
    {
      title: '平均Token',
      dataIndex: 'avg_tokens',
      key: 'avg_tokens',
      render: (avg: number) => <Statistic value={avg} precision={1} suffix="tokens/次" />,
    },
    {
      title: '最后使用',
      dataIndex: 'last_used',
      key: 'last_used',
      render: (date: string) => (
        <Space>
          <ClockCircleOutlined />
          {new Date(date).toLocaleString()}
        </Space>
      ),
    },
  ];

  // 转换模型统计数据
  const modelStatsData = usageStats?.model_stats ? 
    Object.entries(usageStats.model_stats).map(([modelName, stats]) => ({
      key: modelName,
      modelName,
      ...stats,
    })) : [];

  // 每日统计数据
  const dailyStatsData = usageStats?.daily_stats ? 
    Object.values(usageStats.daily_stats)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 7) : [];

  if (!canViewPreferences()) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert
          message="访问受限"
          description="您没有权限访问使用信息页面，请联系管理员申请权限。"
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        <BarChartOutlined /> 使用信息
      </Title>
      <Paragraph type="secondary">
        查看您的AI模型使用详情和统计信息。
      </Paragraph>

      {/* 使用统计 */}
      <Card
        title={
          <Space>
            <BarChartOutlined />
            使用统计
          </Space>
        }
        loading={statsLoading}
        style={{ marginBottom: 24 }}
      >
        {usageStats && (
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Statistic
                title="总调用次数"
                value={usageStats.total_requests}
                prefix={<ApiOutlined />}
              />
            </Col>
            <Col span={12}>
              <Statistic
                title="总Token使用"
                value={usageStats.total_tokens}
                suffix="tokens"
              />
            </Col>
            <Col span={12}>
              <Statistic
                title="总花费"
                value={usageStats.total_cost}
                prefix="¥"
                precision={4}
              />
            </Col>
            <Col span={12}>
              <Statistic
                title="最后更新"
                value={new Date(usageStats.last_updated).toLocaleDateString()}
                prefix={<ClockCircleOutlined />}
              />
            </Col>
          </Row>
        )}
        {/* 每日统计 */}
        {dailyStatsData.length > 0 && (
          <Card
            title="近7日使用情况"
            size="small"
            style={{ marginTop: 16 }}
          >
            {dailyStatsData.map((stat) => (
              <div key={stat.date} style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '8px 0',
                borderBottom: `1px solid ${token.colorBorderSecondary}`,
              }}>
                <Text>{stat.date}</Text>
                <Space>
                  <Tag color="blue">{stat.requests}次</Tag>
                  <Tag color="green">¥{stat.cost.toFixed(4)}</Tag>
                </Space>
              </div>
            ))}
          </Card>
        )}
      </Card>

      {/* 模型使用详情 */}
      {modelStatsData.length > 0 && (
        <Card
          title={
            <Space>
              <RobotOutlined />
              模型使用信息
            </Space>
          }
        >
          <Table
            columns={modelStatsColumns}
            dataSource={modelStatsData}
            pagination={false}
            size="small"
          />
        </Card>
      )}
    </div>
  );
};

export default UsageInfo; 